import { reactive, watch } from 'vue'

import { ACCENTS, DEFAULT_ACCENT } from '@/data/accents'
import { useTheme } from '@/composables/useTheme'

/** Настройки оформления, переключаемые через панель Tweaks. */
export interface Tweaks {
  /** Ключ пресета акцента (его primary-цвет). */
  accent: string
  /** Фоновая подсветка (blob'ы + glow контакт-карты). */
  glow: boolean
  /** Глобальные анимации. */
  anim: boolean
}

const STORAGE_KEY = 'um_tweaks'

const DEFAULTS: Tweaks = { accent: DEFAULT_ACCENT, glow: true, anim: true }

function resolveInitialTweaks(): Tweaks {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === null) return { ...DEFAULTS }
  try {
    const parsed = JSON.parse(saved) as Partial<Tweaks>
    return {
      accent: typeof parsed.accent === 'string' && parsed.accent in ACCENTS ? parsed.accent : DEFAULTS.accent,
      glow: typeof parsed.glow === 'boolean' ? parsed.glow : DEFAULTS.glow,
      anim: typeof parsed.anim === 'boolean' ? parsed.anim : DEFAULTS.anim,
    }
  } catch {
    // Битый JSON в localStorage — откатываемся к дефолтам, ошибку не глотаем молча в проде.
    return { ...DEFAULTS }
  }
}

// Глобальное состояние (singleton).
const tweaks = reactive<Tweaks>(resolveInitialTweaks())

const { theme } = useTheme()

/**
 * Прописывает акцентные CSS-переменные на <html>. Оттенки blob'ов зависят от темы,
 * поэтому акцент переприменяется и при смене темы.
 */
function applyAccent(): void {
  const preset = ACCENTS[tweaks.accent] ?? ACCENTS[DEFAULT_ACCENT]
  if (preset === undefined) return

  const root = document.documentElement.style
  root.setProperty('--c-blue', preset.p)
  root.setProperty('--c-indigo', preset.p)
  root.setProperty('--c-violet', preset.g)
  root.setProperty('--c-cyan', preset.a)
  root.setProperty('--accent-rgb', preset.rgb)
  root.setProperty('--grad', `linear-gradient(120deg, ${preset.p} 0%, ${preset.g} 100%)`)
  root.setProperty('--grad-soft', `linear-gradient(125deg, ${preset.p}, ${preset.g})`)

  const blobs = theme.value === 'light' ? preset.light : preset.dark
  root.setProperty('--blob-a', blobs[0])
  root.setProperty('--blob-b', blobs[1])
  root.setProperty('--blob-c', blobs[2])
}

watch([() => tweaks.accent, theme], applyAccent, { immediate: true })

watch(
  () => tweaks.glow,
  (value) => document.documentElement.setAttribute('data-glow', value ? 'on' : 'off'),
  { immediate: true },
)

watch(
  () => tweaks.anim,
  (value) => document.documentElement.setAttribute('data-anim', value ? 'on' : 'off'),
  { immediate: true },
)

// Персист всего объекта настроек.
watch(
  tweaks,
  (value) => localStorage.setItem(STORAGE_KEY, JSON.stringify(value)),
  { deep: true },
)

/**
 * Управление настройками оформления (акцент, подсветка, анимации).
 * Состояние общее, изменения мгновенно применяются к <html> и сохраняются.
 */
export function useTweaks() {
  function setAccent(accent: string): void {
    tweaks.accent = accent
  }

  function toggleGlow(): void {
    tweaks.glow = !tweaks.glow
  }

  function toggleAnim(): void {
    tweaks.anim = !tweaks.anim
  }

  return { tweaks, setAccent, toggleGlow, toggleAnim }
}
